import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { Input } from '@interlayer/js/components/common/Fields/Input/Input';
import { Select } from '@interlayer/js/components/common/Fields/Select/Select';
import { I18n } from '@interlayer/js/components/common/I18n';
import { Loader } from '@interlayer/js/components/common/Loader/Loader';
import { SelectedAddress } from './components/SelectedAddress';

import { useTranslations } from '@core/js/hooks/i18n';
import { storageService } from '@core/js/services/storage.service';
import { clsb } from '@core/js/utils/classNames.util';

import { useRegistrationContext } from '@interlayer/js/app/components/main/Registration/hocs/context';
import { useFindAddress } from '@interlayer/js/app/hooks/postCodeFinder/hooks/useFindAddress';
import { useGetAddressById } from '@interlayer/js/app/hooks/postCodeFinder/hooks/useGetAddressById';
import { STORED_QUERY_PARAMS } from '@interlayer/js/app/hooks/useStoreQueryParam/constants';
import { VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import { setIsManuallyAddress } from '@interlayer/js/app/store/reducers/Registration/actions';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useClickOutSide } from '@interlayer/js/app/utils/common.util';
import { SELECT_ARROW_ICON } from '@interlayer/js/components/common/Fields/Select/constants.buzz';

import { useNearestClubByPostcode } from './hooks';
import { TAddressPostCodeFieldsProps } from './types';

import style from './AddressPostCodeFields.scss';

export const AddressPostCodeFields: React.FC<TAddressPostCodeFieldsProps> = ({
    className,
    classNames = {},
    handlerSwitchManually,
    isNearestClub = true
}) => {
    const dispatch = useDispatch();
    const form = useForm();
    const inputRef = useRef<HTMLDivElement>(null);
    const { isClubUpgrade } = useRegistrationContext();
    const [isActive, setIsActive] = useState(false);
    const { config: { showSuccessTickIcons } } = useRegistrationSelector();
    const t = useTranslations('playtech.mobile.system.registration.');
    const buildCls = clsb([style, classNames]);
    const hallCode = storageService.local.getItem(STORED_QUERY_PARAMS.hallCode);

    const { values: address, errors } = form.getState();

    useClickOutSide(inputRef, () => setIsActive(false), false);

    useEffect(() => {

        if (hallCode && isNearestClub) {
            form.change(VALIDATION_FIELDS.homeVenue, hallCode);
        }
    }, [form, hallCode, isNearestClub]);

    const userAddress = useMemo(() => {
        return [
            `${address.house || ''} ${address.street || ''}`.trim(),
            address.state,
            address.city,
            address.zip
        ].filter(Boolean);
    }, [address.city, address.house, address.state, address.street, address.zip]);

    const {
        state: { data: findedAddresses, isLoading: isLoadingAddressesData },
        fetchFindAddress
    } = useFindAddress();

    const {
        state: { isLoading: isLoadingAddressData },
        fetchAddressById
    } = useGetAddressById();

    const {
        clubsNearest,
        getNearestClubs,
        isLoadingNearestClub
    } = useNearestClubByPostcode();

    const isLoading = isLoadingAddressesData || isLoadingNearestClub || isLoadingAddressData;
    const isSelectedAddress = !isLoadingAddressData && userAddress.length && !errors?.searchAddress;

    const handlerFetchFinderPlaceByPostcode = (value: string, containerId?: string) => {
        return fetchFindAddress(value, containerId);
    };

    const handlerFinderNearestClubsByPostcode = async (PostalCode: string) => {
        if (isClubUpgrade || !PostalCode || !isNearestClub || hallCode) {
            return;
        }

        const clubs = await getNearestClubs(PostalCode);

        if (clubs) {
            form.change(VALIDATION_FIELDS.homeVenue, clubs[0].BuzzId);
        }
    };

    useEffect(() => {
        handlerFinderNearestClubsByPostcode(address.zip);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (event: React.KeyboardEvent<HTMLInputElement> & { target: HTMLInputElement }) => {
        const { value } = event.target;

        if (event.keyCode === 13 && !form.getState().errors) {
            form.submit();
        }

        if (value.length >= 3) {
            handlerFetchFinderPlaceByPostcode(value);
        }
    };

    const handlerManualAddress = () => {
        handlerSwitchManually(true);
    };

    const handlerOnChangeAddress = async (id: string) => {
        setIsActive(false);

        const response = await fetchAddressById(id);

        if (!response) {
            return;
        }

        const {
            SubBuilding,
            County: state,
            City: city,
            BuildingName,
            Street,
            District,
            BuildingNumber,
            PostalCode: zip
        } = response;

        handlerFinderNearestClubsByPostcode(zip);

        const house = `${SubBuilding} ${BuildingName} ${BuildingNumber}`.trim();
        const street = Street || District;

        form.batch(() => {
            form.change(VALIDATION_FIELDS.house, house);
            form.change(VALIDATION_FIELDS.street, street);
            form.change(VALIDATION_FIELDS.city, city);
            form.change(VALIDATION_FIELDS.state, state);
            form.change(VALIDATION_FIELDS.zip, zip);
        });

        if (!(house && street && city && zip)) {
            handlerManualAddress();
            dispatch(setIsManuallyAddress(true));
            setTimeout(() => form.submit(), 0);
        } else {
            form.blur(VALIDATION_FIELDS.searchAddress);
        }
    };

    const handleAddressClick = (
        id: string,
        isNeedRequest: boolean,
        requestText: string
    ) => (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (isNeedRequest) {
            return handlerFetchFinderPlaceByPostcode(requestText, id);
        }

        handlerOnChangeAddress(id);
    };

    const renderArrow = () => (
        <div
            onClick={() => setIsActive(!isActive)}
            className={style.wrapperInputArrow}
        >
            <SvgIcon
                height="1rem"
                width="1rem"
                type={SELECT_ARROW_ICON}
                className={clsx(style.autocompleteInputArrow, { [style.isActive]: isActive })}
            />
        </div>
    );

    return (
        <div
            className={clsx(style.container, className)}
            data-testid="AddressPostCodeFields__container"
        >
            <div ref={inputRef} className={buildCls(['inputWrap'])}>
                <Input
                    isShowValidationIcon={showSuccessTickIcons}
                    placeholder={t('label.searchAddress')}
                    className={style.input}
                    classNames={{ rightBlock: style.rightBlock }}
                    name={VALIDATION_FIELDS.searchAddress}
                    onChange={onChange}
                    onFocus={() => setIsActive(true)}
                    rightBlock={renderArrow}
                />

                {isActive && (isLoadingAddressesData || Boolean(findedAddresses?.options.length)) && (
                    <div className={style.autocompleteWrapper}>
                        {!isLoadingAddressesData && Boolean(findedAddresses?.options.length) &&
                            findedAddresses?.options.map(({ id, label, isNeedRequest, requestText }) => (
                                <div
                                    key={id}
                                    className={style.autocompleteItem}
                                    onClick={handleAddressClick(id, isNeedRequest, requestText)}
                                >
                                    <span
                                        className={style.autocompleteText}
                                        dangerouslySetInnerHTML={{ __html: label }}
                                    />

                                    {isNeedRequest && (
                                        <SvgIcon
                                            type="arrow-right"
                                            className={style.autocompleteArrow}
                                        />
                                    )}
                                </div>
                            ))
                        }

                        {isLoadingAddressesData && (
                            <Loader />
                        )}
                    </div>
                )}
            </div>

            {isSelectedAddress
                ? (
                    <>
                        <div className={buildCls(['addressWrapper'])}>
                            <I18n
                                t={t}
                                tKey="label.selectAddress"
                                className={style.addressLabel}
                            />

                            <SelectedAddress
                                addressArray={userAddress}
                                className={classNames.addressData}
                            >
                                <div className={style.manualAddress}>
                                    <span
                                        className={style.linkAddress}
                                        onClick={handlerManualAddress}
                                    >
                                        <I18n t={t} tKey="link.notMyAddress" />
                                    </span>
                                </div>
                            </SelectedAddress>
                        </div>

                        {!isLoadingNearestClub && Boolean(clubsNearest?.length) && !isClubUpgrade && isNearestClub && (
                            <Select
                                name="homeVenue"
                                options={clubsNearest}
                                className={style.clubsNearest}
                                label={t('placeholder.nearest')}
                                isValidationIcon={showSuccessTickIcons}
                            />
                        )}
                    </>
                )
                : (
                    <div className={clsx(style.linkAddressWrap, { [style.linkAddressWrapWithError]: errors?.searchAddress })}>
                        <div
                            className={style.linkAddress}
                            onClick={handlerManualAddress}
                        >
                            <I18n t={t} tKey="link.manualAddress" />
                        </div>
                    </div>
                )
            }

            {isLoading && <Loader />}
        </div>
    );
};
